import axios, { AxiosInstance, AxiosRequestConfig, Method } from 'axios';

import { Convert } from "@/app/entity/TokenEntity";
import { storeToken } from "@/app/utils/store";
import { useUserStore } from '@/store/user';
import createAuthRefreshInterceptor from "axios-auth-refresh";

export const DEFAULT_API_BASE_URL = 'https://api.ultikits.com';

/**
 * Resolves the configured API base URL to a usable origin, falling back to
 * `DEFAULT_API_BASE_URL` when unset, empty, or whitespace-only. Any trailing
 * slashes are stripped so callers can safely append a single `/` separator.
 */
export const resolveApiBaseUrl = (configuredBaseUrl?: string | null): string => {
  const trimmedBaseUrl = configuredBaseUrl?.trim();
  return (trimmedBaseUrl || DEFAULT_API_BASE_URL).replace(/\/+$/, '');
};

/**
 * Joins a (possibly overridden) base URL with a request path using exactly
 * one `/` separator, regardless of leading/trailing slashes on either side.
 */
export const composeApiUrl = (configuredBaseUrl: string | null | undefined, path: string): string => {
  const normalizedPath = path.replace(/^\/+/, '');
  return `${resolveApiBaseUrl(configuredBaseUrl)}/${normalizedPath}`;
};

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const apiBaseUrl = resolveApiBaseUrl(configuredApiBaseUrl);

export const getTokenRefreshUrl = (configuredBaseUrl: string | null | undefined = configuredApiBaseUrl): string =>
  composeApiUrl(configuredBaseUrl, '/user/refreshToken');

/**
 * Base API class, all API classes should call this class to send request
 *
 * This class is a wrapper of axios, it provides some useful methods to send request
 *
 * The default baseURL is https://api.ultikits.com unless VITE_API_BASE_URL is
 * set (e.g. for local development or UAT against a local/sandbox Worker API).
 *
 * The default timeout is 5000ms
 *
 * Some properties and logic are hard-coded for the UltiKits api, so you can't use it to send request to other api.
 *
 * The documentation of the UltiKits api is here: https://wisdomme.gitbook.io/ultikits-panel
 *
 * To make a request, you need to call the following methods:
 * - `withPath(path: string)` to set the path of the request
 * - `withParams(params: any)` to set the params of the request
 * - `withMethod(method: Method)` to set the method of the request
 * - `withToken()` if you want to send the request with token
 * - `request()` to send the request, it will return a promise
 *
 * > Note: you should call withPath() and withMethod() at least once before calling request()
 *
 * It is recommended to convert the response to an entity class, There are some entity classes in `src\app\entity`,
 * and you can use `Convert.toXXXEntity()` to convert the response to an entity class.
 *
 * You can also create your own entity class, just create an interface with the same structure as the response, and add the `Convert.toXXXEntity()` method.
 *
 *
 * @example ``` typescript
 * const api = new API();
 * const formData = new FormData();
 * formData.append('username', username);
 * formData.append('password', password);
 *
 * api
 *   .withPath('/user/getToken')
 *   .withBody(formData)
 *   .withMethod("POST")
 *   .request()
 *   .then((response) => {
 *     if (response.data != '') {
 *       resolve(Convert.toTokenEntity(response.data));
 *     } else {
 *       reject(response.data);
 *     }
 *   }, (reason) => {
 *     reject(reason);
 * });
 * ```
 *
 * @class API
 * @author qianmo
 * @version 2.0.0
 * @since 2.0.0
 */
export class API {
  private readonly instance: AxiosInstance;

  private path!: string | null;
  private params: any | null;
  private method!: Method | null;
  private data: any | null;

  private readonly baseURL = apiBaseUrl
  private readonly timeout = 30000

  private userStore = useUserStore();

  /**
   * @constructor Create an instance of API
   *
   * You can create an instance of API by calling `new API()` or `API.getInstance()`
   */
  constructor() {
    this.instance = axios.create();
  }

  withToken(): API {
    this.instance.defaults.headers.common['Authorization'] = 'Bearer ' + this.userStore.auth.token.access;
    createAuthRefreshInterceptor(this.instance, (failedRequest) => {
      // 创建 FormData 对象
      const formData = new FormData();
      formData.append('refresh_token', localStorage.getItem("refresh_token") || '');

      return axios.post(getTokenRefreshUrl(this.baseURL), formData).then((response) => {
        const tokenEntity = Convert.toTokenEntity(response.data);

        // 保存当前用户ID，防止刷新token时丢失
        const currentUserId = localStorage.getItem('id');

        // 如果刷新返回的是系统用户ID (999)，则保持原始用户ID
        if (tokenEntity.id === 999 && currentUserId && currentUserId !== '999') {
          tokenEntity.id = parseInt(currentUserId);
        }

        storeToken(tokenEntity);
        failedRequest.response.config.headers['Authorization'] = `Bearer ${response.data.access_token}`;
        return Promise.resolve();
      }, () => {
        this.userStore.updateAuthStatus(false);
        return Promise.reject();
      });
    });
    return this;
  }

  withPath(path: string): API {
    this.path = path;
    return this;
  }

  withParams(params: any): API {
    this.params = params;
    return this;
  }

  withHeaders(headers: Record<string, string>): API {
    Object.entries(headers).forEach(([key, value]) => {
      this.instance.defaults.headers.common[key] = value;
    });
    return this;
  }

  withBody(body: any): API {
    this.data = body;
    return this;
  }

  /**
   * Set the method of the request
   *
   * The method can be one of the following:
   * - GET (in common use)
   * - POST (in common use)
   * - ...
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods
   *
   *
   * @param method
   */
  withMethod(method: Method): API {
    this.method = method;
    return this;
  }

  request(): Promise<any> {
    if (!this.method) {
      throw new Error('HTTP method is not set. Call withMethod() before request().');
    }

    const path = this.path;
    if (!path) {
      throw new Error('Path is not set. Call withPath() before request().');
    }

    const requestConfig: AxiosRequestConfig = {
      url: composeApiUrl(this.baseURL, path),
      timeout: this.timeout,
      method: this.method,
      params: this.params,
      data: this.data
    };

    // 如果data是对象且不是FormData，则设置Content-Type为application/json
    if (this.data && typeof this.data === 'object' && !(this.data instanceof FormData)) {
      requestConfig.headers = {
        'Content-Type': 'application/json',
        ...requestConfig.headers
      };
    }

    return this.instance.request(requestConfig);
  }
}
